/* eslint-disable prefer-destructuring */
import { FieldNames } from 'Constants/FieldNames'
import { intersection } from 'lodash'
import { ResponseCatalog, ProductRecommendationModel } from 'Models'
import {
	OrderModel,
	BlockKeys,
	consentIcons,
	fieldsByFormBlock,
	ELECTRONIC_CONSENT_STATUS,
	TIconProps,
	TimeSlotsModel,
	ClientType,
	GroupFields,
	ReportOption,
	ReportType,
	ProductsCore,
	isLockedByClientType,
	ProductCardState,
	visitFlagSelectItems,
	visitType,
} from 'Pages/orders_design'
import { ProductSlugs } from 'Constants/ProductSlugs'
import {
	checkRequiredFields,
	NumberFormatRuLocale,
	WrappedFormUtilsExtended,
	isEmptyValue,
} from 'Helpers'
import { FormNavigationTypes } from 'LibNeo'
import { RbidosLeadModel } from 'Pages/lead'
import translate_errors from 'Src/locale/ru/errors.json'
import { WrappedFormUtils } from 'antd/lib/form/Form'
import moment from 'moment/moment'
import { DATE_FORMAT_FOR_BACKEND } from 'Constants/Global'
import { RegionsModel } from 'Pages/referenceBooks'
import { ProductModel } from 'Pages/orders'

const isClient = [FieldNames.ACCOUNT_NUMBER, FieldNames.ACCOUNT_BANK_BIC]

export const getClientTypeByInn = (inn?: string) => {
	if (!inn) return ClientType.ALL
	return inn.length === 12 ? ClientType.IP : ClientType.COMPANY
}

export const requiredOrderFields = (
	key: BlockKeys,
	order: OrderModel,
	form: WrappedFormUtilsExtended
) => {
	if (key === BlockKeys.REASSIGN) {
		return [FieldNames.FILE]
	}
	if (key === BlockKeys.ACCOUNT_BY_SBER_ONLINE) {
		return [FieldNames.SBER_ONLINE]
	}
	if (key === BlockKeys.WITHOUT_BANK || key === BlockKeys.ORDER_DATA) {
		const requiredFieldsByFormBlock = intersection(
			[...(order?.required_fields ?? [])],
			fieldsByFormBlock[key]
		) as FieldNames[]

		if (form.getFieldValue(FieldNames.IS_CLIENT) === true) {
			return [...requiredFieldsByFormBlock, ...isClient]
		}

		return requiredFieldsByFormBlock
	}
}

export const isEmptAddress = (form: WrappedFormUtils): Record<FieldNames, boolean> | {} => {
	const posAddress = form?.getFieldValue(FieldNames.POSADDRESS)
	const emptyAddress =
		!posAddress?.house_type_full ||
		!posAddress?.house ||
		!posAddress?.postal_code ||
		!posAddress?.street ||
		!posAddress?.street_type_full

	return emptyAddress
}

export const getRequiredEmptyFields = (
	key: BlockKeys,
	order: OrderModel,
	form: WrappedFormUtilsExtended
) => {
	const requiredFieldsByFormBlock = intersection(
		[...(order?.required_fields ?? [])],
		fieldsByFormBlock[key]
	) as FieldNames[]

	return requiredFieldsByFormBlock.filter((item) => isEmptyValue(form.getFieldValue(item)))
}

export const filterProductsWithoutQuery = <
	T extends Pick<ProductModel, 'name' | 'available_lk_neo'>
>(
	products: T[],
	flag?: keyof T
): T[] =>
	products?.filter(
		(product): boolean => (!flag || Boolean(product[flag])) && product.available_lk_neo
	)

export const equalityLeadValues = (
	form: WrappedFormUtilsExtended,
	lead: RbidosLeadModel,
	leadValues: FieldNames[]
) => {
	if (!lead) return false
	for (let i = 0; i < leadValues.length; i += 1) {
		if (form?.getFieldValue[i] !== lead?.[i]) return false
	}
	return true
}

export const requiredReserveFields = (order: OrderModel, form?: WrappedFormUtilsExtended) => {
	const managerVisitFlag =
		form.getFieldValue(FieldNames.CM_VISIT_FLAG) === visitType.OUT_OFFICE_VISIT
	if (form?.getFieldValue(FieldNames.CM_VISIT_FLAG) === visitFlagSelectItems[1].id) {
		let newReserveFields = [...GroupFields.ReserveFields]
		if (managerVisitFlag) {
			newReserveFields = GroupFields.ReserveFields.filter(
				(field) => field !== FieldNames.SBER_GEO_UNIT_CODE_NSI
			)
			return [...newReserveFields, ...GroupFields.PaperlessFelds]
		}
		return [...GroupFields.ReserveFields, ...GroupFields.PaperlessFelds]
	}
	return [...GroupFields.ReserveFields]
}

export const getButtonState = (
	item: ProductModel | ProductRecommendationModel,
	inn: string,
	productCore: ProductsCore,
	selectedProductsSet: Set<number>
) => {
	const isLocked = isLockedByClientType(item, getClientTypeByInn(inn))
	if (isLocked) return ProductCardState.LOCKED
	if (selectedProductsSet.has(item?.id)) return ProductCardState.CHOOSEN
	const relatedProducts = productCore.getRelatedProducts(selectedProductsSet)

	const byGroups = Array.from(relatedProducts.byProductGroup)
	const byBlocked = Array.from(relatedProducts.byBlocked)
	const blockProductGroupIds = item?.block_product_group_ids?.map(Number)
	if (
		byGroups.includes(item?.product_group?.slug) ||
		intersection(byBlocked, blockProductGroupIds).length
	) {
		return ProductCardState.CHANGE
	}
	return ProductCardState.ADD
}

export const isSalaryProduct = (order: OrderModel) => {
	return order?.products.some(
		(product) =>
			product.product.slug === ProductSlugs.ZARPLATNYY_PROEKT &&
			order?.online_product_ids.includes(product.product.id)
	)
}

export const isFilledFormBlock = (
	key: BlockKeys,
	form: WrappedFormUtilsExtended,
	order: OrderModel
) => {
	if (key === BlockKeys.REASSIGN) return form?.getFieldValue(FieldNames.FILE)?.length

	if (key === BlockKeys.RESERVE)
		return checkRequiredFields(form, requiredReserveFields(order, form))
	if (key === BlockKeys.ACCOUNT_BY_SBER_ONLINE) return form.getFieldValue(FieldNames.SBER_ONLINE)
	return checkRequiredFields(form, getRequiredEmptyFields(key, order, form))
}

export const isOrderFilledRequiredFields = (
	key: BlockKeys,
	order: OrderModel,
	form: WrappedFormUtilsExtended
): boolean => {
	return getRequiredEmptyFields(key, order, form).every(
		(field) => !isEmptyValue(order[field as keyof OrderModel])
	)
}

export const getNavigationType = (
	order: OrderModel,
	form: WrappedFormUtilsExtended,
	key: BlockKeys
) => {
	if (key === BlockKeys.RESERVE) {
		if (!checkRequiredFields(form, getRequiredEmptyFields(BlockKeys.RESERVE, order, form)))
			return FormNavigationTypes.Locked
		return isFilledFormBlock(BlockKeys.RESERVE, form, order)
			? FormNavigationTypes.Completed
			: FormNavigationTypes.Warning
	}
	if (key === BlockKeys.ACCOUNT_BY_SBER_ONLINE) {
		return isFilledFormBlock(BlockKeys.ACCOUNT_BY_SBER_ONLINE, form, order)
			? FormNavigationTypes.Completed
			: FormNavigationTypes.Error
	}
	if (
		key === BlockKeys.WITHOUT_BANK &&
		isEmptAddress(form) &&
		Boolean(form?.getFieldValue(FieldNames.POSADDRESS))
	) {
		return FormNavigationTypes.Warning
	}
	if (
		key === BlockKeys.ORDER_DATA &&
		(isFilledFormBlock(key, form, order) || isOrderFilledRequiredFields(key, order, form))
	) {
		return FormNavigationTypes.Completed
	}

	const isFilled = isFilledFormBlock(key, form, order)
	if (isFilled) return FormNavigationTypes.Completed
	return FormNavigationTypes.Warning
}

export const changeEmailField = (form, fieldValue: string) => {
	if (form.getFieldValue(FieldNames.UI_FINANCE_PERSON_EMAIL)) {
		form.setFieldsValue({
			[FieldNames.FINANCE_PERSON_EMAIL]: fieldValue ?? undefined,
		})
	}
	if (form.getFieldValue(FieldNames.UI_DECISION_MAKER_EMAIL)) {
		form.setFieldsValue({
			[FieldNames.DECISION_MAKER_EMAIL]: fieldValue ?? undefined,
		})
	}
	if (form.getFieldValue(FieldNames.UI_MANAGER_EMAIL)) {
		form.setFieldsValue({
			[FieldNames.MANAGER_EMAIL]: fieldValue ?? undefined,
		})
	}
}

export const changePhoneField = (form, fieldValue: string) => {
	if (form.getFieldValue(FieldNames.UI_DECISION_MAKER_PHONE)) {
		form.setFieldsValue({
			[FieldNames.DECISION_MAKER_PHONE]: fieldValue ?? undefined,
		})
	}
	if (form.getFieldValue(FieldNames.UI_MANAGER_PHONE)) {
		form.setFieldsValue({
			[FieldNames.MANAGER_PHONE]: fieldValue ?? undefined,
		})
	}
}

export const isUnderfined = (arg): string => arg ?? ''

export const getSelectedProductsSet = (selectedProducts: ProductModel[]): Set<number> =>
	new Set(selectedProducts.map((item) => item.id))

export const getProductsArray = (
	selectedIds: Set<number>,
	products: ResponseCatalog<ProductModel>
): ProductModel[] => {
	const result = []
	Array.from(selectedIds).forEach((id) => {
		products.entries.forEach((product) => {
			if (id === product?.id) {
				result.push(product)
			}
		})
	})
	return result
}

export const getIconConsentStatus = (
	lead: RbidosLeadModel,
	order: OrderModel,
	isPaperAgreement: boolean,
	isReserveAccountActive: boolean,
	isDigitalConfirmationsHasOrders: boolean,
	reserveWithPaperAgreement?: boolean
): TIconProps => {
	if (isPaperAgreement || reserveWithPaperAgreement || !isDigitalConfirmationsHasOrders)
		return consentIcons.success
	switch (lead?.state || order?.lead?.state) {
		case ELECTRONIC_CONSENT_STATUS.NEW:
		case ELECTRONIC_CONSENT_STATUS.SENT:
		case ELECTRONIC_CONSENT_STATUS.ACTIVATED:
			return consentIcons.inProgress
		case ELECTRONIC_CONSENT_STATUS.COMPLETED:
			return isReserveAccountActive && !lead?.first_name
				? consentIcons.inProgress
				: consentIcons.success
		case ELECTRONIC_CONSENT_STATUS.COMPLETED_NOT_ACTUAL:
		case ELECTRONIC_CONSENT_STATUS.WITH_VIOLATION:
			return consentIcons.success
		default:
			return consentIcons.error
	}
}

export const orderTipStatus = (
	lead: RbidosLeadModel,
	isPaperAgreement: boolean,
	reserveWithPaperAgreement?: boolean
) => {
	if (!lead?.first_name && lead?.state === ELECTRONIC_CONSENT_STATUS.COMPLETED) {
		return ELECTRONIC_CONSENT_STATUS.NOT_ACTUAL
	}
	if (isPaperAgreement || reserveWithPaperAgreement) {
		return ELECTRONIC_CONSENT_STATUS.COMPLETED
	}
	return lead?.state
}

export const getTranslatePosaddressErrors = (errors: string[]): string => {
	const translate = errors.map((error) => translate_errors[error])
	return translate.join(', ')
}

export const shortRegionName = (name: string) => {
	const words = ['республика', 'область', 'край', 'ао - югра']
	let newName = name?.toLowerCase()
	if (name) {
		words.forEach((w) => {
			if (newName.includes(w)) newName = newName.replace(w, '')

			if (/^г\./.test(newName)) newName = newName.split('.')[1]
		})
		return newName.trim()
	}
}

export const useAvailableDays = (slots: TimeSlotsModel[]) => {
	const getDateSlots = slots?.map((item) => item.date)
	const available_days = (date: moment.Moment) => {
		const days = (getDateSlots ?? []).find((day) => {
			return day === date.format(DATE_FORMAT_FOR_BACKEND)
		})
		return !days
	}
	return available_days
}

export const appointmentFieldsData = (values, fullAddress) => {
	const time = values[FieldNames.TIME]?.split(',')
	const geo_lon_lat = fullAddress.key?.split(',')
	return {
		[FieldNames.DATE]: values[FieldNames.DATE].format(DATE_FORMAT_FOR_BACKEND),
		[FieldNames.TIME_FROM]: time[0],
		[FieldNames.TIME_TO]: time[1],
		[FieldNames.CITY]: values[FieldNames.CITY],
		[FieldNames.REGION]: values[FieldNames.REGION],
		[FieldNames.LONGITUDE]: geo_lon_lat[1],
		[FieldNames.LATITUDE]: geo_lon_lat[0],
		[FieldNames.DESCRIPTION]: values[FieldNames.ADDRESS_DADATA],
	}
}

export const getErrorMessage = (error_message: string, data?: string[]) => {
	return data.reduce((acc, item) => {
		return acc.replace(':data', item || '')
	}, error_message)
}

export const returnCountOrders = (total: number) => {
	const numberTotal = NumberFormatRuLocale(total)
	let countTotal = '...'
	if (numberTotal) {
		countTotal = total > 200000 ? `примерно ${numberTotal}` : numberTotal
	}
	return countTotal
}

export const getFilteredOptions = (options: ReportOption[], isDzoReportVisible: boolean) => {
	return options.filter((option) => isDzoReportVisible || option.value !== ReportType.DZO_ORDERS)
}

export const getCurrentRegionForGeoUnit = (regions: RegionsModel[], shortRegionName: string) => {
	return regions?.length
		? (regions?.length === 1
				? regions[0]
				: regions?.find((item) => item.name.toLowerCase().startsWith(shortRegionName))
		  )?.id
		: null
}
